import * as React from 'react';

import { useSettingsLocation } from '@rematter/modules/settings/_state/settingsModule.state';
import {
  Button,
  DataGrid,
  ListScreenLayout,
  Pagination,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import * as state from '../../../_state/itemAliasesList.state';
import { useTranslations } from '../../../i18n/useTranslations';
import {
  enumItemAliasSortOption,
  ItemAliasRecord,
  ItemAliasSortOption,
} from '../../../types/inventoryItem';

import { useItemAliasesPanelColumns } from './hooks/useItemAliasesColumns';
import { ItemAliasCreateDialog } from './ItemAliasCreateDialog';
import { ItemAliasesFilters } from './ItemAliasesFilters';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: ItemAliasSortOption; desc: ItemAliasSortOption }
> = {
  aliasName: {
    asc: enumItemAliasSortOption.AliasNameAsc,
    desc: enumItemAliasSortOption.AliasNameDesc,
  },
  aliasType: {
    asc: enumItemAliasSortOption.AliasTypeAsc,
    desc: enumItemAliasSortOption.AliasTypeDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ ITEM ALIASES LIST VIEW ⚛️ ⚛️ ⚛️
//
export const ItemAliasesListView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  // Atoms
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteItemAliases,
  );
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  // Callbacks
  const handleRowClick = React.useCallback(
    (itemAlias: ItemAliasRecord) => {
      setLocation(`/inventory/item-aliases/${itemAlias.id}`);
    },
    [setLocation],
  );
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumItemAliasSortOption.AliasNameAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleCreateClick = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  // Other data hooks
  const { columns, columnVisibilityState } = useItemAliasesPanelColumns({
    onRowClick: handleRowClick,
  });

  const itemAliases = data?.itemAliases ?? [];
  const total = data?.totalCount ?? 0;
  const emptyPanelProps = {
    title: t.inventoryView.itemAliasesPanel.emptyState.title,
    description: t.inventoryView.itemAliasesPanel.emptyState.description,
    action: (
      <Button
        onPress={handleCreateClick}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.inventoryView.itemAliasesPanel.actions.create}
      </Button>
    ),
    centered: true,
  };

  return (
    <ListScreenLayout
      filters={<ItemAliasesFilters />}
      actions={
        <Button
          variant="solid"
          color="primary"
          icon={<LucidePlusCircle />}
          onPress={handleCreateClick}
        >
          {t.inventoryView.itemAliasesPanel.actions.create}
        </Button>
      }
      footer={
        total > limit ? (
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        ) : undefined
      }
      className="@ItemAliasesListView"
    >
      <DataGrid<ItemAliasRecord>
        isLoading={loadingState.isInitialLoading || isFetching}
        columns={columns}
        columnVisibilityState={columnVisibilityState}
        dataSource={itemAliases}
        rowKey="id"
        aria-label={t.inventoryView.itemAliasesPanel.ariaLabel}
        selectionMode="multiple"
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
        onColumnSort={handleColumnSort}
        className="w-full text-sm"
        fullWidth
        hasError={!!error}
        errorPanelProps={
          error
            ? {
                description:
                  t.inventoryView.itemAliasesPanel.errors.loadingFailed(
                    error.message,
                  ),
                centered: true,
              }
            : undefined
        }
        emptyPanelProps={emptyPanelProps}
      />
      <ItemAliasCreateDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={() => refetch?.()}
      />
    </ListScreenLayout>
  );
};
